import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  Stack,
  Grid,
} from "@mui/material";
import {
  AutoAwesomeOutlined as MRLIcon,
  AutoAwesome as SparkleIcon,
  LocalFireDepartment as HotIcon,
  ShieldOutlined as FairIcon,
  EmojiEventsOutlined as PrizeIcon,
  ConfirmationNumberOutlined as TicketIcon,
  CasinoOutlined as DiceIcon,
} from "@mui/icons-material";
import { colors, borderRadiusPx } from "../../../../../theme";
import { Path, DEFAULT_CURRENCY, LOTTERY_NAMES } from "../../../../../static/Constants";
import { CommonChip, CommonBreadcrumbs } from "../../../../Components/ui";

const MegaHeroBanner = ({
  totalActive = 0,
  combinedPrize = "0.00",
  lowestEntry = "0.00",
  currency = DEFAULT_CURRENCY,
}) => {
  return (
    <Box sx={{ mb: 4 }}>
      {/* Breadcrumbs Navigation */}
      <CommonBreadcrumbs
        items={[
          { label: "Home", path: Path.root },
          { label: "All Lotteries", path: Path.allLotteries },
          { label: `${LOTTERY_NAMES.MEGABET} (MRL)` },
        ]}
      />


      {/* Hero Banner Box */}
      <Box className="mega-hero-banner" sx={{ p: { xs: 3, md: 4 } }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={7}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.5}
              sx={{ mb: 1.5 }}
            >
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: borderRadiusPx.md,
                  bgcolor: colors.emerald[50],
                  color: colors.emerald[600],
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 2px 8px rgba(16, 185, 129, 0.2)",
                }}
              >
                <MRLIcon sx={{ fontSize: 26 }} />
              </Box>
              <div>
                <Typography
                  variant="overline"
                  sx={{
                    color: colors.emerald[600],
                    fontWeight: 700,
                    letterSpacing: 1.2,
                  }}
                >
                  MULTI-RANDOM LOTTERY ARENA
                </Typography>
                <Typography variant="h4" className="mega-hero-title">
                  {`Autobet ${LOTTERY_NAMES.MEGABET}`}
                </Typography>
              </div>
            </Stack>

            <Typography
              variant="body1"
              sx={{
                color: colors.slate[600],
                maxWidth: 540,
                lineHeight: 1.6,
                mb: 2.5,
              }}
            >
              Pick your custom combination of lucky numbers from the active matrix boards. Compete for massive rollover accumulator jackpots resolved via provably fair Chainlink VRF!
            </Typography>

            <Stack direction="row" gap={1.25} flexWrap="wrap">
              <CommonChip
                icon={<DiceIcon sx={{ fontSize: 16, color: `${colors.emerald[600]} !important` }} />}
                label="Multi-Number Picking"
                size="small"
                sx={{
                  bgcolor: colors.emerald[50],
                  color: colors.emerald[700],
                  border: `1px solid ${colors.emerald[200]}`,
                  fontWeight: 600,
                }}
              />
              <CommonChip
                icon={<HotIcon sx={{ fontSize: 15, color: `${colors.emerald[600]} !important` }} />}
                label="Accumulator Rollover Jackpots"
                size="small"
                sx={{
                  bgcolor: colors.emerald[50],
                  color: colors.emerald[700],
                  border: `1px solid ${colors.emerald[200]}`,
                  fontWeight: 600,
                }}
              />
              <CommonChip
                icon={<FairIcon sx={{ fontSize: 15, color: `${colors.emerald[600]} !important` }} />}
                label="Provably Fair VRF"
                size="small"
                sx={{
                  bgcolor: colors.emerald[50],
                  color: colors.emerald[700],
                  border: `1px solid ${colors.emerald[200]}`,
                  fontWeight: 600,
                }}
              />
              <CommonChip
                icon={<SparkleIcon sx={{ fontSize: 15, color: `${colors.emerald[600]} !important` }} />}
                label="Instant Smart Contract Payouts"
                size="small"
                sx={{
                  bgcolor: colors.emerald[50],
                  color: colors.emerald[700],
                  border: `1px solid ${colors.emerald[200]}`,
                  fontWeight: 600,
                }}
              />
            </Stack>
          </Grid>

          {/* Quick Metrics Cards */}
          <Grid item xs={12} md={5}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Box className="mega-stat-card">
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <HotIcon sx={{ fontSize: 18, color: colors.emerald[600] }} />
                    <Typography
                      variant="caption"
                      sx={{ color: colors.slate[500], fontWeight: 600 }}
                    >
                      {`Active ${LOTTERY_NAMES.MEGABET}s`}
                    </Typography>
                  </Stack>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 800, color: colors.slate[900] }}
                  >
                    {totalActive}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: colors.emerald[600], fontWeight: 600 }}
                  >
                    ● Live on-chain
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={6}>
                <Box className="mega-stat-card">
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <PrizeIcon sx={{ fontSize: 18, color: colors.amber[600] }} />
                    <Typography
                      variant="caption"
                      sx={{ color: colors.slate[500], fontWeight: 600 }}
                    >
                      Total Prize Pool
                    </Typography>
                  </Stack>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 800, color: colors.emerald[700] }}
                  >
                    {combinedPrize} {currency}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: colors.slate[400], fontWeight: 500 }}
                  >
                    Across all rounds
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={6}>
                <Box className="mega-stat-card">
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <TicketIcon sx={{ fontSize: 18, color: colors.blue[600] }} />
                    <Typography
                      variant="caption"
                      sx={{ color: colors.slate[500], fontWeight: 600 }}
                    >
                      Entry From
                    </Typography>
                  </Stack>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, color: colors.slate[900] }}
                  >
                    {lowestEntry} {currency}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: colors.slate[400], fontWeight: 500 }}
                  >
                    Per single ticket
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={6}>
                <Box className="mega-stat-card">
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <FairIcon sx={{ fontSize: 18, color: colors.emerald[600] }} />
                    <Typography
                      variant="caption"
                      sx={{ color: colors.slate[500], fontWeight: 600 }}
                    >
                      Decentralized
                    </Typography>
                  </Stack>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, color: colors.slate[900] }}
                  >
                    100% On-Chain
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: colors.slate[400], fontWeight: 500 }}
                  >
                    Trustless execution
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

MegaHeroBanner.propTypes = {
  totalActive: PropTypes.number,
  combinedPrize: PropTypes.string,
  lowestEntry: PropTypes.string,
  currency: PropTypes.string,
};

export default MegaHeroBanner;
